import { numberParser } from 'factorial-form'
import { groupBy } from 'lodash'
import isFinite from 'lodash/isFinite'

import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { Salary, SalaryPeriodicity } from './salary'

export interface SalaryByWorkplaceGroup {
  groupKey: string
  workplaceLabel: string
  formattedRange: string
  referenceSalary?: Salary
}

// This uses the same logic as Factorial Forms because
// contracts uses factorial-forms cents
// and salary range is used for contracts
// if that change this should change too

export function salaryToCents(salary: string): null | number {
  const number = numberParser(salary)
  return number != null && isFinite(number) ? toCents(number) : null
}

export function salaryToString(salaryInCents: number) {
  return fromCents(salaryInCents).toString()
}

export function formatSalaryAmount(
  salaryAmount: number | undefined,
  country: string | undefined,
  currency: string | undefined,
  fractionDigits = 2
) {
  if (!salaryAmount) return ''

  return Intl.NumberFormat(country, {
    style: 'currency',
    currency,
    maximumFractionDigits: fractionDigits,
  }).format(salaryAmount)
}

export function formatSalaryFromCents(
  salaryAmountInCents: number | undefined,
  country: string | undefined,
  currency: string | undefined,
  fractionDigits = 2
) {
  if (!salaryAmountInCents) return ''

  return formatSalaryAmount(fromCents(salaryAmountInCents), country, currency, fractionDigits)
}

export function isSalaryOnRange(
  salaryAmountInCents: number | null | undefined,
  salaryFrequency: string | null | undefined,
  rangeFrequency: string | null | undefined,
  rangeMin: number | null | undefined,
  rangeMax: number | null | undefined,
  amountType?: 'fixed' | 'range'
) {
  if (!salaryAmountInCents || !salaryFrequency) return false

  if (rangeFrequency !== salaryFrequency) return false

  if (amountType === 'range') {
    if (!rangeMin || !rangeMax) return false
    return (
      fromCents(salaryAmountInCents) >= fromCents(rangeMin) &&
      fromCents(salaryAmountInCents) <= fromCents(rangeMax)
    )
  }

  if (amountType === 'fixed') {
    if (!rangeMin) return false
    return fromCents(salaryAmountInCents) == fromCents(rangeMin)
  }
}

export const formatSalaryFromCentsObject = (salary?: Salary): string => {
  if (!salary) return ''
  const { gross, min, max, currency } = salary

  if (gross != null) {
    return formatSalaryFromCents(gross, undefined, currency)
  } else {
    return `${formatSalaryFromCents(min, undefined, currency)} - ${formatSalaryFromCents(
      max,
      undefined,
      currency
    )}`
  }
}

const formatSalary = (salary?: Salary): string => {
  return formatSalaryFromCentsObject(salary)
}
export const formatSalariesFromCentsObject = (salaries: Salary[]): string => {
  if (salaries?.length === 0) return ''

  const summarizedSalariesByCurrency = getSummarizedSalariesByCurrency(salaries)
  const preferredSalary = getPreferredSalary(summarizedSalariesByCurrency)

  return formatSalaryFromCentsObject(preferredSalary)
}

const formatSalariesWithFrequency = (salaries: Salary[]): string => {
  if (salaries.length === 0) return ''
  const summarizedSalariesByCurrency = getSummarizedSalariesByCurrency(salaries)
  const preferredSalary = getPreferredSalary(summarizedSalariesByCurrency)
  return formatSalary(preferredSalary)
}

export const getSummarizedSalariesByCurrency = (salaries: Salary[]): Salary[] => {
  const summarizedSalaries: Salary[] = []
  Object.values(groupSalariesByCurrency(salaries)).forEach((salariesByCurrency) => {
    const summarizedSalary = summarizeSalaries(salariesByCurrency)
    if (summarizedSalary) summarizedSalaries.push(summarizedSalary)
  })
  return summarizedSalaries
}

export const getPreferredSalary = (salaries: Salary[]) => {
  if (salaries.length === 0) return
  const preferredSalary = salaries.find((salary) => salary.currency === defaultCurrency)
  return preferredSalary ?? salaries[0]
}

const summarizeSalaries = (salaries: Salary[]): Salary | undefined => {
  const salary = salaries[0]
  if (!salary) return
  const [minAmount, maxAmount] = getRangeFromSalaries(salaries)
  return {
    ...salary,
    min: minAmount,
    max: maxAmount,
    periodicity: SalaryPeriodicity.yearly,
    gross: undefined,
  }
}

const getRangeFromSalaries = (salaries: Salary[]): [number, number] => {
  const convertedSalaries = salaries.map(convertSalaryToPreferredFrequency)

  let minSalaryAmount: number = Number.MAX_VALUE
  let maxSalaryAmount: number = Number.MIN_VALUE

  convertedSalaries.forEach((salary) => {
    if (salary.min && salary.min < minSalaryAmount) {
      minSalaryAmount = salary.min
    }
    if (salary.max && salary.max > maxSalaryAmount) {
      maxSalaryAmount = salary.max
    }
    if (salary.gross && salary.gross < minSalaryAmount) {
      minSalaryAmount = salary.gross
    }
    if (salary.gross && salary.gross > maxSalaryAmount) {
      maxSalaryAmount = salary.gross
    }
  })
  return [minSalaryAmount, maxSalaryAmount]
}

const convertSalaryToPreferredFrequency = (salary: Salary): Salary => {
  const convertedSalary = {
    ...salary,
  }
  if (convertedSalary.gross) {
    convertedSalary.gross = convertSalaryAmountToYearly(
      convertedSalary.gross,
      convertedSalary.periodicity
    )
  }
  if (convertedSalary.min) {
    convertedSalary.min = convertSalaryAmountToYearly(
      convertedSalary.min,
      convertedSalary.periodicity
    )
  }
  if (convertedSalary.max) {
    convertedSalary.max = convertSalaryAmountToYearly(
      convertedSalary.max,
      convertedSalary.periodicity
    )
  }
  return convertedSalary
}

export const getSalaryFrequencyText = (
  frequency?: string,
  options?: { singular?: boolean }
): string => {
  if (options?.singular) {
    switch (frequency) {
      case SalaryPeriodicity.hourly:
        return i18n.t('job_catalog.nodes.salaries.frequencies.hourly_singular')
      case SalaryPeriodicity.daily:
        return i18n.t('job_catalog.nodes.salaries.frequencies.daily_singular')
      case SalaryPeriodicity.weekly:
        return i18n.t('job_catalog.nodes.salaries.frequencies.weekly_singular')
      case SalaryPeriodicity.monthly:
        return i18n.t('job_catalog.nodes.salaries.frequencies.monthly_singular')
      case SalaryPeriodicity.yearly:
      default:
        return i18n.t('job_catalog.nodes.salaries.frequencies.yearly_singular')
    }
  }

  switch (frequency) {
    case SalaryPeriodicity.hourly:
      return i18n.t('job_catalog.nodes.salaries.frequencies.hourly')
    case SalaryPeriodicity.daily:
      return i18n.t('job_catalog.nodes.salaries.frequencies.daily')
    case SalaryPeriodicity.weekly:
      return i18n.t('job_catalog.nodes.salaries.frequencies.weekly')
    case SalaryPeriodicity.monthly:
      return i18n.t('job_catalog.nodes.salaries.frequencies.monthly')
    case SalaryPeriodicity.yearly:
      return i18n.t('job_catalog.nodes.salaries.frequencies.yearly')
    default:
      return i18n.t('job_catalog.nodes.salaries.frequencies.yearly')
  }
}

const groupSalariesByCurrency = (salaries: Salary[]): Record<string, Salary[]> => {
  return groupBy(salaries, 'currency')
}

/**
 * Groups salaries by workplace set and returns yearly-formatted ranges for display.
 * Monthly salaries are converted to yearly (×12). Used in sidepanel salary section.
 */
export function getSalariesGroupedByWorkplace(salaries: Salary[]): SalaryByWorkplaceGroup[] {
  if (!salaries.length) return []

  const groupKeyToSalaries = new Map<string, Salary[]>()
  const groupKeyToWorkplaceName = new Map<string, string>()

  for (const salary of salaries) {
    const hasWorkplaces = salary.workplaces && salary.workplaces.length > 0

    if (!hasWorkplaces) {
      const defaultKey = 'default'
      if (!groupKeyToSalaries.has(defaultKey)) {
        groupKeyToSalaries.set(defaultKey, [])
        groupKeyToWorkplaceName.set(defaultKey, i18n.t('job_catalog.nodes.salaries.all_workplaces'))
      }
      groupKeyToSalaries.get(defaultKey)?.push(salary)
      continue
    }

    const workplacesById = new Map(
      (salary.workplaces || []).map((workplace) => [workplace.id.toString(), workplace])
    )

    for (const [workplaceId, workplace] of workplacesById) {
      const groupKey = workplaceId
      if (!groupKeyToSalaries.has(groupKey)) {
        groupKeyToSalaries.set(groupKey, [])
        groupKeyToWorkplaceName.set(groupKey, workplace.name)
      }
      groupKeyToSalaries.get(groupKey)?.push(salary)
    }
  }

  const result: SalaryByWorkplaceGroup[] = []

  const allWorkplacesLabel = i18n.t('job_catalog.nodes.salaries.all_workplaces')

  for (const [key, groupSalaries] of groupKeyToSalaries) {
    const summarizedByCurrency = getSummarizedSalariesByCurrency(groupSalaries)
    const preferred = getPreferredSalary(summarizedByCurrency)
    let formattedRange = ''
    if (preferred) {
      if (preferred.gross != null) {
        formattedRange = i18n.formatNumber(preferred.gross / 100, 'currency', preferred.currency)
      } else if (
        preferred.min != null &&
        preferred.max != null &&
        preferred.min === preferred.max
      ) {
        formattedRange = i18n.formatNumber(preferred.min / 100, 'currency', preferred.currency)
      } else {
        const { min, max, currency } = preferred
        formattedRange = `${i18n.formatNumber((min ?? 0) / 100, 'currency', currency)} - ${i18n.formatNumber((max ?? 0) / 100, 'currency', currency)}`
      }
    }
    const workplaceLabel =
      key === 'default' ? allWorkplacesLabel : (groupKeyToWorkplaceName.get(key) ?? '')
    result.push({ groupKey: key, workplaceLabel, formattedRange, referenceSalary: preferred })
  }

  result.sort((a, b) => a.workplaceLabel.localeCompare(b.workplaceLabel))

  return result
}

// TODO: This needs to be changed once we have different preferred frequencies for salaries
// https://factorialmakers.atlassian.net/browse/FCT-34978
const convertSalaryAmountToYearly = (amount: number, periodicity: string): number => {
  switch (periodicity) {
    case SalaryPeriodicity.yearly:
      return amount
    case SalaryPeriodicity.monthly:
      return amount * WORK_MONTHS_PER_YEAR
    case SalaryPeriodicity.weekly:
      return amount * WORK_WEEKS_PER_YEAR
    case SalaryPeriodicity.daily:
      return amount * WORK_DAYS_PER_YEAR
    case SalaryPeriodicity.hourly:
      return amount * WORK_HOURS_PER_YEAR
    default:
      return amount
  }
}

// TODO: we need to connect all these values with the working conditions
const WORK_MONTHS_PER_YEAR = 12
const WORK_WEEKS_PER_YEAR = 52
const WORK_DAYS_PER_YEAR = 225
const WORK_HOURS_PER_YEAR = 1800

// TODO: set default currency for company
// https://factorialmakers.atlassian.net/browse/FCT-34880
export const defaultCurrency = 'EUR'
// TODO: set default frequency for company
// https://factorialmakers.atlassian.net/browse/FCT-34978
export const defaultPeriodicity = SalaryPeriodicity.yearly

/**
 * Selects the appropriate salary range based on currency and location criteria.
 *
 * @param salaryRanges - Array of salary ranges to choose from
 * @param locationId - Optional location ID to match against workplaces
 * @param currency - Currency to match against salary range currency
 * @returns The matching salary range or null if no match is found
 */
export function selectReferenceSalaryRange(
  salaryRanges: Salary[],
  locationId: string | null | undefined,
  currency: string
): Salary | undefined {
  // Filter by location criteria first
  let filteredRanges: Salary[]
  filteredRanges = filterByLocation(salaryRanges, locationId)
  filteredRanges = filterByCurrency(filteredRanges, currency)
  return filteredRanges.length > 0 ? filteredRanges[0] : undefined
}

function filterByLocation(salaryRanges: Salary[], locationId: string | null | undefined): Salary[] {
  let filteredRanges: Salary[]
  // If locationId provided, first try to find ranges that contain this location
  if (locationId) {
    filteredRanges = salaryRanges.filter((range) =>
      range.workplaces?.some((workplace) => String(workplace.id) === locationId)
    )
    if (filteredRanges.length > 0) {
      return filteredRanges
    }
  }

  // If no locationId provided, or no salary range found for the specific location,
  // fallback to salary ranges associated to no workplace (all workplaces/other workplaces)
  return salaryRanges.filter((range) => !range.workplaces || range.workplaces.length === 0)
}

function filterByCurrency(salaryRanges: Salary[], currency: string): Salary[] {
  return salaryRanges.filter((range) => range.currency === currency)
}

/**
 * Calculates the recommended value for a salary based on its type.
 *
 * @param salary - The salary object (FixedSalary or SalaryRange)
 * @returns The recommended value, or undefined if salary is invalid
 */
export function getRecommendedSalaryValue(salary: Salary | undefined): number | undefined {
  if (!salary) return undefined

  // If it's a FixedSalary, return the gross value
  if ('gross' in salary && salary.gross != null) {
    return salary.gross
  }

  // If it's a SalaryRange, calculate the midpoint
  if ('min' in salary && 'max' in salary && salary.min != null && salary.max != null) {
    return Number(salary.min) + (salary.max - salary.min) / 2
  }

  return undefined
}

/**
 * Validates if a value is valid for a given Salary.
 *
 * @param salary - The salary object to validate against (values in cents)
 * @param value - The value to validate (in decimal format)
 * @param periodicity - The periodicity of the value
 * @returns true if the value is valid for the salary, false otherwise
 */
export function isValidSalaryValue(salary: Salary, value: number, periodicity: string): boolean {
  // Check if periodicity matches
  if (salary.periodicity !== periodicity) {
    return false
  }

  // Check if it's a FixedSalary
  if ('gross' in salary && salary.gross != null) {
    return value === fromCents(salary.gross)
  }

  // Check if it's a SalaryRange
  if ('min' in salary && 'max' in salary && salary.min != null && salary.max != null) {
    return value >= fromCents(salary.min) && value <= fromCents(salary.max)
  }

  return false
}
